import { useState, useRef, useEffect } from 'react'
import { teacherPriorityApi } from '@/services/api/teacherPriority'
import { apiClient, handleApiRequest } from '@/services/api/client'
import { PrepAttachmentResponse } from '@/types/teacher-request'

interface PrepFileUploadProps {
  requestId: string
  prepId: string | null
  attachments: PrepAttachmentResponse[]
  pendingFiles?: File[]
  onPendingFilesChange?: (files: File[]) => void
  onUploadComplete: () => void
  isReadOnly?: boolean
}

const DEFAULT_PREP_SIZE = 10 * 1024 * 1024
const ALLOWED_TYPES = ['.pdf', '.docx', '.doc', '.xlsx', '.xls', '.png', '.jpg', '.jpeg']

export function PrepFileUpload({ requestId, prepId, attachments, pendingFiles = [], onPendingFilesChange, onUploadComplete, isReadOnly }: PrepFileUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [maxPrepSize, setMaxPrepSize] = useState(DEFAULT_PREP_SIZE)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    handleApiRequest<{ prep_max_size_bytes: number }>(apiClient.get('/api/v1/files/limits'))
      .then((res) => {
        if (res?.prep_max_size_bytes) setMaxPrepSize(res.prep_max_size_bytes)
      })
      .catch(() => undefined)
  }, [])

  const totalSize = attachments.reduce((sum, att) => sum + att.file_size, 0) + pendingFiles.reduce((sum, f) => sum + f.size, 0)
  const maxMb = Math.round(maxPrepSize / (1024 * 1024))

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return

    setError(null)
    let currentTotalSize = totalSize
    const newPending: File[] = []

    for (const file of Array.from(files)) {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase()
      if (!ALLOWED_TYPES.includes(ext)) {
        setError(`Ungültiges Dateiformat: ${file.name}. Erlaubt: ${ALLOWED_TYPES.join(', ')}`)
        break
      }

      if (currentTotalSize + file.size > maxPrepSize) {
        setError(`Maximale Anhangsgröße von ${maxMb} MB überschritten bei: ${file.name}`)
        break
      }

      currentTotalSize += file.size

      if (prepId) {
        setUploading(true)
        try {
          await teacherPriorityApi.uploadPrepAttachment(requestId, prepId, file)
          onUploadComplete()
        } catch (e: any) {
          setError(e?.detail || `Upload fehlgeschlagen: ${file.name}`)
          break
        } finally {
          setUploading(false)
        }
      } else {
        newPending.push(file)
      }
    }

    if (newPending.length > 0 && onPendingFilesChange) {
      onPendingFilesChange([...pendingFiles, ...newPending])
    }

    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRemovePendingFile = (index: number) => {
    if (onPendingFilesChange) {
      onPendingFilesChange(pendingFiles.filter((_, i) => i !== index))
    }
  }

  const handleDelete = async (attachmentId: string) => {
    if (!prepId) return
    if (!confirm('Anhang wirklich entfernen?')) return
    try {
      await teacherPriorityApi.deletePrepAttachment(requestId, prepId, attachmentId)
      onUploadComplete()
    } catch (e: any) {
      setError(e?.detail || 'Löschen fehlgeschlagen')
    }
  }

  const handleDownload = async (attachmentId: string, filename: string) => {
    if (!prepId) return
    try {
      const blob = await teacherPriorityApi.downloadPrepAttachment(requestId, prepId, attachmentId)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (e: any) {
      setError(e?.detail || 'Download fehlgeschlagen')
    }
  }

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  }

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const files = e.dataTransfer.files
    await handleFileSelect(files)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
        Angehängte Dateien ({formatSize(totalSize)} / {maxMb} MB)
      </label>

      {!isReadOnly && (
        <div
          className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-blue-400 transition cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={e => handleFileSelect(e.target.files)}
            accept={ALLOWED_TYPES.join(',')}
          />
          {uploading ? (
            <p className="text-xs text-blue-500">Wird hochgeladen...</p>
          ) : (
            <>
              <p className="text-xs text-gray-500">Dateien hierher ziehen oder <span className="text-blue-500 font-medium">durchsuchen</span></p>
              <p className="text-[10px] text-gray-400 mt-1">PDF, DOCX, XLSX, Bilder · max. {maxMb} MB gesamt</p>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">{error}</div>
      )}

      {pendingFiles.length > 0 && (
        <div className="mt-2 space-y-1">
          {pendingFiles.map((file, index) => (
            <div key={`pending-${index}`} className="flex items-center gap-2 p-2 bg-yellow-50 border border-dashed border-yellow-200 rounded-lg">
              <span className="text-lg">📄</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-gray-700 truncate">{file.name}</div>
                <div className="text-[10px] text-gray-400">{formatSize(file.size)} · wird beim Speichern hochgeladen</div>
              </div>
              {!isReadOnly && (
                <button
                  aria-label="Entfernen"
                  onClick={() => handleRemovePendingFile(index)}
                  className="text-gray-400 hover:text-red-500 text-xs"
                  title="Entfernen"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {attachments.length > 0 && (
        <div className="mt-2 space-y-1">
          {attachments.map(att => (
            <div key={att.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <span className="text-lg">📄</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-gray-700 truncate">{att.filename}</div>
                <div className="text-[10px] text-gray-400">{formatSize(att.file_size)}</div>
              </div>
              <button
                aria-label="Herunterladen"
                onClick={() => handleDownload(att.id, att.filename)}
                className="text-gray-400 hover:text-blue-500 text-xs"
                title="Herunterladen"
              >
                ⬇
              </button>
              {!isReadOnly && (
                <button
                  aria-label="Löschen"
                  onClick={() => handleDelete(att.id)}
                  className="text-gray-400 hover:text-red-500 text-xs"
                  title="Löschen"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
